import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useGetSessionToolTitle } from '@backrs_ui/grow-ui/src/hooks/useSessionToolTitle';
import {
  FollowUpButton,
  isStructuredSession,
  LubavSessionType,
  SUPERSESSION_TITLES,
  SessionSwitchAction,
} from '@backrs_ui/grow-ui/src/models/models';
import { openSignupPrompt } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { addOfficeHoursMessage } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import {
  officeHoursApi,
  type SessionStatus,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { resetOfficeHoursSession } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  clearSessionSwitch,
  setActiveSessionType,
  setPendingStructuredChoice,
  setPreviewedSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  createSessionAction,
  getActiveLubavSession,
  getPendingSessionSwitch,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  getSeedStatus,
  getSessionStatusEntry,
} from '@backrs_ui/grow-ui/src/redux/sessionStatuses/sessionStatuses';
import { store } from '@backrs_ui/grow-ui/src/redux/store';
import { getTabKey } from '@backrs_ui/grow-ui/src/redux/tabs/tabKey';
import { setSelectedTabKey } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';
import { IS_SANDBOX, isSandboxStaff } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

const STRINGS = appStrings.modules.officeHours.sessionSwitch;

// The choices a target session offers, by status.
const SWITCH_ACTIONS: Record<SessionStatus, readonly SessionSwitchAction[]> = {
  none: ['start'],
  mid: ['continue', 'restart'],
  complete: ['discuss', 'restart'],
};

const chatLabel = (action: SessionSwitchAction, title: string): string => {
  switch (action) {
    case 'start':
      return STRINGS.chatActions.start(title);
    case 'continue':
      return STRINGS.chatActions.continue(title);
    case 'restart':
      return STRINGS.chatActions.restart(title);
    case 'discuss':
      return STRINGS.chatActions.discuss;
  }
};

const buildSwitchButton = (
  target: LubavSessionType,
  action: SessionSwitchAction,
  title: string,
): FollowUpButton => ({
  id: `session-switch-${target}-${action}`,
  label: chatLabel(action, title),
  sessionSwitch: { targetSession: target, action },
});

const buildSwitchPrompt = (
  target: LubavSessionType,
  status: SessionStatus,
  title: string,
): { text: string; buttons: FollowUpButton[] } => ({
  text: STRINGS.prompt[status](title),
  buttons: SWITCH_ACTIONS[status].map((action) => buildSwitchButton(target, action, title)),
});

type SwitchModalAction = Readonly<{ label: string; id: string; onClick: () => void }>;

export type SessionSwitchModal = Readonly<{
  headerText: string;
  bodyText: string;
  primaryAction: SwitchModalAction;
  secondaryAction?: SwitchModalAction;
}>;

// Watches for a requested session switch and surfaces it: an in-chat prompt from an
// office-hours session, a Leave/Cancel modal from a structured session.
export const useSessionSwitchPrompt = (): {
  switchModal: SessionSwitchModal | null;
  dismissSwitch: () => void;
} => {
  const dispatch = useAppDispatch();
  const { isGuest } = useAuth();
  const pendingSessionSwitch = useAppSelector(getPendingSessionSwitch);
  const activeSession = useAppSelector(getActiveLubavSession);
  const activeSessionId = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav.sessionId,
  );
  const seedStatus = useAppSelector(getSeedStatus);
  const getSessionToolTitle = useGetSessionToolTitle();
  const resolveTitle = useCallback(
    (target: LubavSessionType): string =>
      getSessionToolTitle(target) ?? SUPERSESSION_TITLES[target] ?? STRINGS.fallbackTitle,
    [getSessionToolTitle],
  );
  const handlingRef = useRef<LubavSessionType | null>(null);
  const [modalTarget, setModalTarget] = useState<{
    fromSession: LubavSessionType;
    target: LubavSessionType;
  } | null>(null);

  // Read status directly from the slice; no per-switch fetch.
  const resolveStatus = useCallback((target: LubavSessionType): SessionStatus => {
    if (!isStructuredSession(target)) return 'none';

    return getSessionStatusEntry(store.getState(), target)?.status ?? 'none';
  }, []);

  // Prefetch the full session data for continue/discuss.
  const warmSession = useCallback(
    (target: LubavSessionType, status: SessionStatus) => {
      if (status === 'none' || !isStructuredSession(target)) return;

      const sessionId = getSessionStatusEntry(store.getState(), target)?.sessionId;

      if (!sessionId) return;

      dispatch(
        officeHoursApi.endpoints.getOfficeHoursSessionData.initiate(
          { session_id: sessionId },
          { forceRefetch: true },
        ),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (!pendingSessionSwitch) {
      handlingRef.current = null;

      return;
    }

    if (handlingRef.current === pendingSessionSwitch) return;

    if (pendingSessionSwitch === activeSession) {
      handlingRef.current = pendingSessionSwitch;
      dispatch(clearSessionSwitch());

      return;
    }

    const target = pendingSessionSwitch;
    const fromSession = activeSession;
    const fromSessionId = activeSessionId;

    // Guests and staff sandbox visitors can't enter a structured session; prompt instead of switching.
    if ((isGuest || isSandboxStaff()) && isStructuredSession(target)) {
      handlingRef.current = pendingSessionSwitch;
      dispatch(openSignupPrompt(IS_SANDBOX ? 'sandbox' : undefined));
      dispatch(clearSessionSwitch());

      return;
    }

    // Leaving a structured session: the Leave/Cancel modal doesn't depend on the target's
    // status, so show it right away and resolve status only once the user confirms.
    if (isStructuredSession(fromSession)) {
      handlingRef.current = pendingSessionSwitch;
      setModalTarget({ fromSession, target });
      dispatch(clearSessionSwitch());

      return;
    }

    // Leaving an office-hours session: the prompt buttons depend on the target's status, so
    // wait for the initial seed to finish before deciding which to show.
    if (seedStatus === 'seeding') return;

    handlingRef.current = pendingSessionSwitch;

    void (async () => {
      const status = resolveStatus(target);
      const { text, buttons } = buildSwitchPrompt(target, status, resolveTitle(target));

      // Surface the target's tab and banner alongside the prompt so the click has a visible
      // effect on the right; the session itself stays untouched until the user picks.
      if (isStructuredSession(target)) {
        dispatch(setPreviewedSession(target));
        dispatch(setSelectedTabKey(getTabKey({ kind: 'resource', boxType: 'FEATURED' })));
      }

      if (fromSession) {
        dispatch(
          createSessionAction(
            addOfficeHoursMessage,
            fromSession,
          )({ text, sender: 'ai', followUpButtons: buttons }),
        );

        if (fromSessionId) {
          await dispatch(
            officeHoursApi.endpoints.appendMessage.initiate({
              session_id: fromSessionId,
              role: 'ai',
              text,
            }),
          );
        }
      }

      dispatch(clearSessionSwitch());
      warmSession(target, status);
    })();
  }, [
    pendingSessionSwitch,
    activeSession,
    activeSessionId,
    isGuest,
    seedStatus,
    resolveStatus,
    resolveTitle,
    warmSession,
    dispatch,
  ]);

  const dismissSwitch = useCallback(() => setModalTarget(null), []);

  // Confirming "Leave" resets the target to a clean slate, switches to it, and shows the
  // start/continue choice as a transient (unpersisted) message. The gate holds the target
  // unloaded until the user picks a button.
  const leaveForSwitch = useCallback(() => {
    if (!modalTarget) return;

    const { target } = modalTarget;

    // Switch into the target now; the pending-choice gate holds it unloaded until the user picks.
    dispatch(resetOfficeHoursSession({ activeSession: target }));
    dispatch(setActiveSessionType(target));
    dispatch(setPendingStructuredChoice(target));
    dispatch(setSelectedTabKey(getTabKey({ kind: 'resource', boxType: 'FEATURED' })));
    setModalTarget(null);

    // The start/continue prompt's buttons depend on the target's status (read from the slice).
    const status = resolveStatus(target);
    const { text, buttons } = buildSwitchPrompt(target, status, resolveTitle(target));

    dispatch(
      createSessionAction(
        addOfficeHoursMessage,
        target,
      )({ text, sender: 'ai', followUpButtons: buttons }),
    );
    warmSession(target, status);
  }, [modalTarget, resolveStatus, resolveTitle, warmSession, dispatch]);

  const switchModal = useMemo<SessionSwitchModal | null>(() => {
    if (!modalTarget) return null;

    return {
      headerText: STRINGS.leaveModal.header(resolveTitle(modalTarget.fromSession)),
      bodyText: STRINGS.leaveModal.body,
      primaryAction: {
        label: STRINGS.leaveModal.leave,
        id: 'session-switch-leave',
        onClick: leaveForSwitch,
      },
      secondaryAction: {
        label: STRINGS.leaveModal.cancel,
        id: 'session-switch-cancel',
        onClick: dismissSwitch,
      },
    };
  }, [modalTarget, leaveForSwitch, dismissSwitch, resolveTitle]);

  return { switchModal, dismissSwitch };
};
